/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import { ButtonBase, Typography2, useFocusManager } from '@coursera/cds-core';

import { useToggleTabContext } from 'bundles/cds-labs/components/ToggleTab/hooks/useToggleTabContext';
import type { ToggleTabValue } from 'bundles/cds-labs/components/ToggleTab/types';

export type ToggleTabProps = {
  value: ToggleTabValue;
  label: string;
  className?: string;
};

const styles = {
  tab: css`
    position: relative;
    padding: var(--cds-spacing-150) var(--cds-spacing-200);
    border-radius: var(--cds-border-radius-max);
    min-width: 100px;
    color: inherit;
    background-color: transparent;
    z-index: 2;
    transition: color 0.1s ease-in-out;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    &:focus::after {
      border-radius: 100px;
    }

    &:hover span {
      text-decoration: underline;
    }

    &.toggle-tab-active {
      color: var(--cds-color-neutral-background-primary);
    }

    &.toggle-tab-invert {
      color: var(--cds-color-neutral-primary-invert);
    }

    &.toggle-tab-active.toggle-tab-invert {
      color: var(--cds-color-neutral-primary);
    }
  `,
  label: css`
    white-space: nowrap;
    transition: color 0.1s ease-in-out;
    color: var(--cds-color-neutral-primary-weak);

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    .toggle-tab-active & {
      color: var(--cds-color-neutral-primary-invert);
    }

    .toggle-tab-invert & {
      color: var(--cds-color-neutral-primary-invert);
    }

    .toggle-tab-active.toggle-tab-invert & {
      color: var(--cds-color-neutral-primary);
    }
  `,
};

const ToggleTab = React.forwardRef<HTMLButtonElement, ToggleTabProps>(({ value, label, className }, ref) => {
  const focusManager = useFocusManager();

  const { value: selectedValue, invert, id, onChange, registerTabRef } = useToggleTabContext();

  // Create internal ref if no ref is passed from parent
  const internalRef = React.useRef<HTMLButtonElement>(null);
  const tabRef = (ref as React.RefObject<HTMLButtonElement>) || internalRef;

  const isSelected = React.useMemo(() => selectedValue === value, [selectedValue, value]);

  const { tabId, panelId } = React.useMemo(
    () => ({
      tabId: `${id}-tab-${value}`,
      panelId: `${id}-panel-${value}`,
    }),
    [id, value]
  );

  // Register this tab's ref when component mounts or value changes
  React.useLayoutEffect(() => {
    registerTabRef(value, tabRef);
  }, [value, tabRef, registerTabRef]);

  const handleKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLButtonElement>>(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          focusManager?.focusNext({ wrap: true });
          break;
        case 'ArrowLeft':
          event.preventDefault();
          focusManager?.focusPrevious({ wrap: true });
          break;
        default:
          // No action needed for other keys
          break;
      }
    },
    [focusManager]
  );

  return (
    <ButtonBase
      ref={tabRef}
      css={styles.tab}
      className={classNames(className, {
        'toggle-tab-active': isSelected,
        'toggle-tab-invert': invert,
      })}
      onClick={() => onChange(value)}
      onKeyDown={handleKeyDown}
      role="tab"
      aria-selected={isSelected}
      aria-controls={panelId}
      id={tabId}
      tabIndex={isSelected ? 0 : -1}
    >
      <Typography2 css={styles.label} variant="actionSecondary" component="span">
        {label}
      </Typography2>
    </ButtonBase>
  );
});

export default ToggleTab;
